import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, Building2, CalendarDays, Loader2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import AxiosInstance from "@/utils/AxiosInstance";
import { getErrToast, getPromise, getPromiseToast } from "@/utils/helpers";
import {
  type LinkedProfile,
  clearUserSwitchSessionData,
  getLoggedTypeOrDefault,
  writeLoggedType,
} from "@/types/linkedProfile";
import { useAuth } from "@/contexts/use-auth";
import TermsConditionsDialog from "@/components/TermsDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function profileLabel(p: LinkedProfile) {
  return [p.firstName, p?.middleName, p.lastName].filter(Boolean).join(" ").trim() || p.patientId;
}

function profileInitials(p: LinkedProfile) {
  return [p.firstName, p.lastName].filter(Boolean).map(name => name.charAt(0).toUpperCase()).join("").slice(0, 2);
}

/** Normalize DOB to YYYY-MM-DD for comparison (avoids timezone shift). */
function normalizeDobToYmd(dob: string | undefined | null): string {
  if (!dob) return "";
  const s = String(dob).trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  return s;
}

/** Display YYYY-MM-DD as MM-DD-YYYY in the native date input overlay. */
function formatYmdAsMdy(ymd: string): string {
  const normalized = normalizeDobToYmd(ymd);
  if (!normalized) return "";
  const [year, month, day] = normalized.split("-");
  return `${month}-${day}-${year}`;
}

function openNativeDatePicker(el: HTMLInputElement | null) {
  if (el?.showPicker) {
    try {
      el.showPicker();
    } catch {
      el.focus();
    }
  } else {
    el?.focus();
  }
}

function ProfileSwitchCard({
  profile,
  id,
  selectedId,
  programs,
}: {
  profile: LinkedProfile;
  id: string;
  selectedId: string;
  programs: string[];
}) {
  const isSelected = selectedId === profile.id;

  return (
    <label
      key={profile.id}
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-2xl border bg-card p-4 transition-all",
        isSelected
          ? "border-primary shadow-sm ring-1 ring-primary/15"
          : "border-border/80 hover:border-muted-foreground/25 hover:bg-muted/30",
      )}
    >
      <RadioGroupItem
        value={profile.id}
        id={id}
        className="mt-3.5 shrink-0 border-muted-foreground/40"
      />
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
        // style={{
        //   background:
        //     "linear-gradient(135deg, oklch(55% .15 250), oklch(70% .14 160))",
        // }}
        aria-hidden
      >
        {/* <User className="h-6 w-6 text-white" strokeWidth={1.75} /> */}
        <Button
                  variant="ghost"
                  size="icon"
                  className="h-12 w-12 rounded-full bg-gradient-to-br from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-bold text-lg shadow-lg"
                >
                  {profileInitials(profile)}
                </Button>
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold capitalize leading-tight text-foreground">
            {profileLabel(profile)}
          </span>
          {/* {programs.map((program: string) => {
            const badge = getStatusBadge(program) || {};
            return (
              <Badge
                key={program}
                variant="outline"
                className="rounded-full border px-2 py-0 text-[10px] font-semibold uppercase tracking-wide"
                style={{
                  backgroundColor: badge.bgColor,
                  color: badge.mainColor,
                  borderColor: badge.mainColor,
                }}
              >
                {program}
              </Badge>
            );
          })} */}
          {/* Patient Id */}
          <div className="text-xs text-muted-foreground">
            <span className="truncate">{profile.patientId}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
        {profile.organizationName && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Building2 className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate">{profile.organizationName}</span>
          </div>
        )}
        </div>

        {/* {(profile.email || profile.primaryNumber) && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
            {profile.email && (
              <HoverTooltip content={profile.email} side="top">
                <div className="flex flex-1 items-center gap-1.5">
                  <Mail className="h-4 w-4 shrink-0 opacity-70" />
                  <span className="truncate">{profile.email}</span>
                </div>
              </HoverTooltip>
            )}
            {profile.primaryNumber && (
              <HoverTooltip
                content={formatUSPhoneNumber(profile.primaryNumber)}
                side="top"
              >
                <div className="flex  flex-1 items-center gap-1.5">
                  <Phone className="h-4 w-4 shrink-0 opacity-70" />
                  <span className="whitespace-nowrap">
                    {formatUSPhoneNumber(profile.primaryNumber)}
                  </span>
                </div>
              </HoverTooltip>
            )}
          </div>
        )} */}
      </div>
    </label>
  );
}

type UserSwitchProps = {
  embedded?: boolean;
  currentProfile?: any;
  onClose?: () => void;
};

export default function UserSwitch({ embedded = false, onClose, currentProfile  }: UserSwitchProps) {

  const navigate = useNavigate();

  const { userSwitchSessionData, setUserSwitchSessionData , signIn } = useAuth();

  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [userData, setUserData] = useState({
    user: null,
    role: null,
    token: null,
    permissions: null,
    profile: null,
  });

  const token = userSwitchSessionData?.token;
  const linkedProfiles = userSwitchSessionData?.linkedProfiles;
  const [selectedId, setSelectedId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDobDialogOpen, setIsDobDialogOpen] = useState(false);
  const [dobInput, setDobInput] = useState("");
  const [dobError, setDobError] = useState("");
  const dobInputRef = useRef<HTMLInputElement>(null);

  const selectedProfile = useMemo(
    () => linkedProfiles?.find((p: LinkedProfile) => p.id === selectedId),
    [linkedProfiles, selectedId],
  );

  useEffect(() => {
    setSelectedId(currentProfile?._id || currentProfile?.id || "");
  }, [currentProfile?._id, currentProfile?.id]);
  // const profiles = useMemo(() => session?.linkedProfiles ?? [], [session]);

  // useEffect(() => {
  //   if (!session) return;
  //   if (profiles.length <= 1) {
  //     const target = session.redirectTo || "/patient-dashboard";
  //     if (session.token) {
  //       localStorage.setItem("userdatatoken", session.token);
  //     }
  //     clearPendingAuthSession();
  //     navigate(target, { replace: true });
  //   }
  // }, [session, profiles.length, navigate]);

  async function handleAgreeTerms() {
    try {
      const { data } = await AxiosInstance().post(
        "/auth/update-consent",
        {},
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
          },
        },
      );
      await signIn(
        { ...userData?.user as any, role: userData?.role },
        userData?.token,
        userData?.permissions,
        // userData?.profile,
      );
      if(!embedded){
        navigate('/patient-dashboard' , { replace: true });
      }
      // if(embedded){
      //   onClose?.();
      // }
    } catch (error) {
      getErrToast(error);
    }
  }

  async function handleContinue() {
    if (!token || !selectedId) return;

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setIsSubmitting(true);

      if (!!selectedId) {
        const switchType = getLoggedTypeOrDefault(
          userSwitchSessionData?.loggedType,
        );
        const { data } : any = await AxiosInstance().post(
          "/auth/switch-profile",
          { patientId: selectedId, type: switchType },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      
        if (
          !data?.data?.user?.hasGivenConsent &&
          data?.data?.user?.role === "patient"
        ) {
          setIsTermsOpen(true);
          setUserData({
            user: data?.data?.user,
            role: data?.data?.user?.role,
            token: data?.data?.token,
            permissions: data?.data?.permissions,
            profile: data?.data?.profile,
          });
        } else {
          const loggedType =
            (data?.data?.type === "phone" || data?.data?.type === "email"
              ? data.data.type
              : switchType) as "email" | "phone";
          writeLoggedType(loggedType);

          await signIn(
            { ...data?.data?.user, role: data?.data?.user?.role || "admin" },
            data?.data?.token,
            data?.data?.permissions,
            data?.data?.profile,
            data?.data?.linkedProfiles || [],
          );
          // navigate("/auth", { replace: true });
          if(embedded){
            onClose?.();
          }
          else{
            navigate('/auth' , { replace: true });
          }
        }
      }
      
      clearUserSwitchSessionData();
      if(embedded){
        resolveFn("User switched successfully...");
      }else{
        resolveFn("Login sucessfully...");
      }
      // navigate("/patient-dashboard", { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      rejectFn(
        err?.response?.data?.message ||
          err?.message ||
          "Could not switch account. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleSwitchClick() {
    if (!selectedId || selectedId === currentProfile?._id) return;

    const profileDob = normalizeDobToYmd(selectedProfile?.dateOfBirth);
    if (!profileDob) {
      toast.error("Date of birth is not available for this profile. Please contact support.");
      return;
    }

    setDobInput("");
    setDobError("");
    setIsDobDialogOpen(true);
  }

  function handleDobVerify() {
    const profileDob = normalizeDobToYmd(selectedProfile?.dateOfBirth);
    const enteredDob = normalizeDobToYmd(dobInput);

    if (!enteredDob) {
      setDobError("Please enter your date of birth.");
      return;
    }

    if (enteredDob !== profileDob) {
      setDobError("Date of birth does not match. Please try again.");
      return;
    }

    setIsDobDialogOpen(false);
    setDobError("");
    void handleContinue();
  }

  // if (!session || profiles.length <= 1) {
  //   return (
  //     <div className="min-h-dvh flex items-center justify-center p-4">
  //       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  //     </div>
  //   );
  // }

  return (
    <div
      className={
        embedded
          ? "flex h-full min-h-0 w-full flex-col bg-transparent p-0 m-0"
          : "min-h-dvh flex flex-col items-center justify-center p-4 bg-muted/30"
      }
    >
      <Card
        className={cn(
          "mx-auto w-full border shadow-md",
          embedded
            ? "flex max-h-[min(70dvh,620px)] min-h-0 max-w-none flex-col"
            : "max-w-lg",
        )}
      >
        <CardHeader className="shrink-0 text-center pb-2">
          {/* Logo */}
          {!embedded && (
          <div className="flex justify-center mb-2">
            <img
              src="/logo.png"
              alt="Anthro Care"
              className="h-14 w-auto max-w-[200px] object-contain"
            />
          </div>
          )}
          <CardTitle className="text-xl">Choose an account</CardTitle>
{embedded ? null : <CardDescription>
            This mobile/email is linked to more than one profile. Select which one you
            want to use.
          </CardDescription>
          }
        </CardHeader>
        <CardContent
          className={cn(
            embedded
              ? "flex min-h-0 flex-1 flex-col gap-4 pb-4 pt-0"
              : "space-y-6 pb-6",
          )}
        >
          {/* {isDemo && (
            <p className="text-xs text-center text-muted-foreground rounded-md border border-dashed px-3 py-2">
              Preview mode with sample profiles. After login, real accounts will
              appear here.
            </p>
          )} */}
          <div
            className={cn(
              embedded
                ? "min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"
                : "max-h-[300px] overflow-y-auto pr-0.5",
            )}
          >

          {Array.isArray(linkedProfiles) && linkedProfiles?.length > 0 ? (
            <RadioGroup
              value={selectedId}
              onValueChange={setSelectedId}
              className="flex flex-col gap-3"
            >
              {linkedProfiles.map((profile: LinkedProfile) => {
                const id = `profile-${profile.id}`;
                const programs = Array.isArray(profile.programs)
                  ? profile.programs
                  : [];
                return (
                  <ProfileSwitchCard
                    key={profile.id}
                    profile={profile}
                    id={id}
                    selectedId={selectedId}
                    programs={programs}
                  />
                );
              })}
            </RadioGroup>
          ) : (
            <p className="text-center text-muted-foreground">No profiles found</p>
          )}
          </div>

          <div className="flex shrink-0 flex-col gap-2 pt-1">
            <Button
              type="button"
              className="inline-flex w-full items-center justify-center gap-2"
              size="lg"
              disabled={!selectedId || isSubmitting || selectedId === currentProfile?._id}
              onClick={handleSwitchClick}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Switch Account
                  {!embedded && <ArrowRight className="h-4 w-4" />}
                </>
              )}
            </Button>

            {!embedded && (
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => navigate("/auth")}
              >
                Back to sign in
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <TermsConditionsDialog
        open={isTermsOpen}
        setOpen={setIsTermsOpen}
        agreeTerms={handleAgreeTerms}
      />

      <Dialog
        open={isDobDialogOpen}
        onOpenChange={(open) => {
          setIsDobDialogOpen(open);
          if (!open) {
            setDobInput("");
            setDobError("");
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Verify date of birth</DialogTitle>
            <DialogDescription>
              Enter the date of birth for{" "}
              <span className="font-medium text-foreground">
                {selectedProfile ? profileLabel(selectedProfile) : "this account"}
              </span>{" "}
              to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="dob-verify">Date of birth</Label>
            <div
              className="relative h-10 w-full cursor-pointer rounded-md border border-input bg-background shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50"
              onClick={() => openNativeDatePicker(dobInputRef.current)}
            >
              <div className="pointer-events-none absolute inset-0 z-0 flex items-center px-3 pr-10 text-sm">
                {dobInput ? (
                  formatYmdAsMdy(dobInput)
                ) : (
                  <span className="text-muted-foreground">MM-DD-YYYY</span>
                )}
              </div>
              <input
                ref={dobInputRef}
                id="dob-verify"
                type="date"
                tabIndex={-1}
                className="absolute inset-0 z-1 h-full w-full cursor-pointer opacity-0 date-input-native"
                value={dobInput}
                max={new Date().toISOString().slice(0, 10)}
                onClick={() => openNativeDatePicker(dobInputRef.current)}
                onChange={(e) => {
                  setDobInput(e.target.value);
                  if (dobError) setDobError("");
                }}
                aria-label="Date of birth"
              />
              <CalendarDays className="pointer-events-none absolute right-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
            {dobError ? (
              <p className="text-sm text-destructive">{dobError}</p>
            ) : null}
          </div>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setIsDobDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="inline-flex w-full items-center justify-center gap-2 sm:w-auto"
              onClick={handleDobVerify}
              disabled={isSubmitting || !dobInput}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify & continue"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}